import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DESIGN } from '../constants';
import { ensureFonts, isFontAvailable } from '../lib/fonts';
import { fitPreviewHeight, readViewportHeight } from '../lib/layout';
import { hitTest, renderThumbnail } from '../lib/render';
import type { LoadedImage } from '../state/useImage';
import type { EditorState, LayerBox, LayerId } from '../types';

/**
 * Tracks the height the browser can actually show, so the preview can be capped
 * against it. `visualViewport` fires on URL-bar collapse, orientation changes
 * and the on-screen keyboard; the window events cover browsers without it.
 */
function useViewportHeight(): number {
  const [height, setHeight] = useState(readViewportHeight);

  useEffect(() => {
    const update = () => setHeight(readViewportHeight());
    update();

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      viewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return height;
}

interface Props {
  state: EditorState;
  image: LoadedImage | null;
  selection: LayerId | null;
  onSelect: (id: LayerId | null) => void;
  /** Deltas are in design-space px, matching TextLayer.x / TextLayer.y. */
  onMove: (id: LayerId, dx: number, dy: number) => void;
  emptyText: string;
  ariaLabel: string;
  /** Upper bound on a roomy screen; the viewport can still force it smaller. */
  maxHeight?: number;
}

export function ThumbnailCanvas({
  state,
  image,
  selection,
  onSelect,
  onMove,
  emptyText,
  ariaLabel,
  maxHeight = 560,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const boxesRef = useRef<LayerBox[]>([]);
  const dragRef = useRef<{ id: LayerId; x: number; y: number; pointerId: number } | null>(null);

  const [cssWidth, setCssWidth] = useState(0);
  const [fontTick, setFontTick] = useState(0);

  const design = DESIGN[state.ratio];
  const aspect = design.width / design.height;

  // Track the available width; the canvas is always laid out from its container
  // so the same component works in a sidebar, full width, or on a phone.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      setCssWidth(width);
    });
    observer.observe(element);
    setCssWidth(element.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  // Canvas text falls back to sans-serif until the webfont is ready, so redraw
  // once the two fonts in use have actually loaded.
  useEffect(() => {
    const specs = [
      { family: state.main.fontFamily, weight: state.main.fontWeight },
      { family: state.sub.fontFamily, weight: state.sub.fontWeight },
    ];
    // Already-loaded fonts need no redraw, which keeps re-selecting a font from
    // costing an extra render.
    if (specs.every((spec) => isFontAvailable(spec.family, spec.weight))) return;

    let cancelled = false;
    void (async () => {
      await ensureFonts(specs);
      if (!cancelled) setFontTick((tick) => tick + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [state.main.fontFamily, state.main.fontWeight, state.sub.fontFamily, state.sub.fontWeight]);

  const viewportHeight = useViewportHeight();
  const heightBudget = fitPreviewHeight(viewportHeight, maxHeight);
  const displayWidth = Math.min(cssWidth, heightBudget * aspect);
  const displayHeight = displayWidth / aspect;

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || displayWidth <= 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const pixelWidth = Math.round(displayWidth * dpr);
    const pixelHeight = Math.round(displayHeight * dpr);
    if (canvas.width !== pixelWidth) canvas.width = pixelWidth;
    if (canvas.height !== pixelHeight) canvas.height = pixelHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    boxesRef.current = renderThumbnail(ctx, {
      state,
      image: image?.source ?? null,
      imageWidth: image?.width ?? 0,
      imageHeight: image?.height ?? 0,
      width: pixelWidth,
      height: pixelHeight,
      selection,
    });
  }, [state, image, displayWidth, displayHeight, selection, fontTick]);

  const toDesign = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const scale = design.width / rect.width;
      return { x: (clientX - rect.left) * scale, y: (clientY - rect.top) * scale };
    },
    [design.width],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const point = toDesign(event.clientX, event.clientY);
    if (!point) return;
    const hit = hitTest(boxesRef.current, point.x, point.y);
    onSelect(hit);
    canvasRef.current?.focus();
    if (!hit) return;
    dragRef.current = { id: hit, x: point.x, y: point.y, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const point = toDesign(event.clientX, event.clientY);
    if (!point) return;
    const dx = point.x - drag.x;
    const dy = point.y - drag.y;
    drag.x = point.x;
    drag.y = point.y;
    onMove(drag.id, dx, dy);
  };

  const endDrag = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLCanvasElement>) => {
    if (event.key === 'Escape') {
      onSelect(null);
      return;
    }
    if (event.key === 'Tab' && !event.shiftKey && selection === null) return;
    const step = event.shiftKey ? 10 : 1;
    const deltas: Record<string, [number, number]> = {
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
    };
    const delta = deltas[event.key];
    if (!delta || !selection) return;
    event.preventDefault();
    onMove(selection, delta[0], delta[1]);
  };

  const hasContent = image !== null || state.main.text.trim().length > 0;

  return (
    <Box ref={containerRef} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ position: 'relative', width: displayWidth || '100%', maxWidth: '100%' }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={handleKeyDown}
          style={{
            width: displayWidth ? `${displayWidth}px` : '100%',
            height: displayHeight ? `${displayHeight}px` : 'auto',
            display: 'block',
            borderRadius: 12,
            touchAction: 'none',
            cursor: selection ? 'grab' : 'default',
          }}
        />
        {!hasContent && (
          <Typography
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              px: 4,
              textAlign: 'center',
              color: 'text.secondary',
              pointerEvents: 'none',
            }}
          >
            {emptyText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
